// tes

import { datadogLogs } from "@datadog/browser-logs";
import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { t, useTranslation } from "@redotech/redo-i18n";
import {
  InteractiveMapLocation,
  ShipmentMethod,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { COMMON } from "@redotech/redo-model/localization/language";
import { Resource } from "@redotech/redo-model/localization/resource";
import { Channel } from "@redotech/redo-model/return";
import { assertNever } from "@redotech/util/type";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { ShippingEstimateContext } from "../../contexts/shipping-estimate";
import {
  getStepsInReturn,
  navigateFromStack,
  Navigator,
  RouteName,
  StepInReturn,
} from "../../navigator";
import { CustomerPortalAppSettings } from "../../settings";
import { ShipmentMethodOption } from "../../shipment-method/util";
import { PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP } from "../../util";
import { formatPriceAdjustment } from "../../utils/fee-and-bonus";
import {
  getShippingMethodOptions as getShippingMethodOptionsUtil,
  isMethodToDisplay,
  MethodToDisplay,
} from "./get-shipping-method-options";

const DECIDE_TO_OFFER_PICKUP_UPSELL_METRICS_LOG_STAMP = "decideToOfferPickup";
const DECIDE_NOT_TO_OFFER_PICKUP_UPSELL_METRICS_LOG_STAMP =
  "decideNotToOfferPickup";

export enum IconType {
  SELF_SHIP = "self ship",
  IN_STORE = "in store",
  BLUE_YONDER = "blue yonder",
  RETURN_BEAR = "return bear",
  DROPOFF = "dropoff",
}

export interface ShipmentMethodItem {
  title: string;
  subtitle: React.ReactNode;
  subtitle2?: string;
  fee?: string;
  selection: ShipmentMethod;
  icon: IconType;
  rightTitle?: string;
  selected: boolean;
  onSelect: () => void;
  locations?: InteractiveMapLocation[];
  privacyPolicyText?: string;
}

interface useShipmentMethodResponse {
  texts: {
    continueText: string;
    previousPage: string;
    howYouWant: string;
    pickBestMethod: string;
  };
  selectedMethod: ShipmentMethod | undefined;
  setSelectedMethod: (method: ShipmentMethod | undefined) => void;
  shouldDisableSelectShipmentMethod: boolean;
  onSelectShipmentMethod: (method?: ShipmentMethod) => void;
  shipmentMethodOptions: ShipmentMethodItem[];
  rawShipmentMethodOptions: ShipmentMethodOption[];
  isLoadingShipmentOptions: boolean;
  shipmentMethodError: string | undefined;
  submitting: boolean;
}

function localizeCopyResourceText(
  resourceText: string | undefined,
  defaultText: string,
): string {
  const text = resourceText ?? defaultText;

  switch (text) {
    case "Pickup":
      return t("Pickup");
    case "Return in store":
      return t("Return in store");
    case "Box and ship it back to us":
      return t("Box and ship it back to us");
    case "Box up your empties and ship them back to us":
      return t("Box up your empties and ship them back to us");
    case "Happy returns":
      return t("Happy returns");
    case "Box-free return location":
      return t("Box-free return location");
    case "Settlement":
      return t("Settlement");
    case "Custom label":
      return t("Custom label");
    case "Pickup from my doorstep":
      return t("Pickup from my doorstep");
    case "Drop it off at a nearby store":
      return t("Drop it off at a nearby store");
    case "Print a shipping label and bring it to a carrier":
      return t("Print a shipping label and bring it to a carrier");
    case "You'll receive a shipping label in your email shortly.":
      return t("You'll receive a shipping label in your email shortly.");
    case "":
      return "";
    case "Take it to one of our nearby locations. No need for packaging or shipping.":
      return t(
        "Take it to one of our nearby locations. No need for packaging or shipping.",
      );
    case "We can’t provide a return label for your location, but don’t worry, we’ll give you step-by-step instructions on the next page for sending your items back.":
      return t(
        "We can’t provide a return label for your location, but don’t worry, we’ll give you step-by-step instructions on the next page for sending your items back.",
      );
    case "Closest box-free locations:":
      return t("Closest box-free locations:");
    default:
      return text;
  }
}

export const getBlueYonderLocationsText = (copyResource?: Resource): string =>
  localizeCopyResourceText(
    copyResource?.ShipmentMethodName_BLUE_YONDER_LOCATIONS_TEXT,
    COMMON.ShipmentMethodName_BLUE_YONDER_LOCATIONS_TEXT,
  );

export const getShipmentMethodTypeToTitleText = (
  copyResource?: Resource,
): Record<ShipmentMethodName, string> => ({
  [ShipmentMethodName.PICKUP]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_PICKUP_TITLE,
    COMMON.ShipmentMethodName_PICKUP_TITLE,
  ),
  [ShipmentMethodName.IN_STORE]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_IN_STORE_TITLE,
    COMMON.ShipmentMethodName_IN_STORE_TITLE,
  ),
  [ShipmentMethodName.SELF_SHIP]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_SELF_SHIP_TITLE,
    COMMON.ShipmentMethodName_SELF_SHIP_TITLE,
  ),
  [ShipmentMethodName.HAPPY_RETURNS]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_HAPPY_RETURNS_TITLE,
    COMMON.ShipmentMethodName_HAPPY_RETURNS_TITLE,
  ),
  [ShipmentMethodName.BLUE_YONDER]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_BLUE_YONDER_TITLE,
    COMMON.ShipmentMethodName_BLUE_YONDER_TITLE,
  ),
  [ShipmentMethodName.RETURN_BEAR]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_RETURN_BEAR_TITLE,
    COMMON.ShipmentMethodName_RETURN_BEAR_TITLE,
  ),
  [ShipmentMethodName.SETTLEMENT]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_SETTLEMENT_TITLE,
    COMMON.ShipmentMethodName_SETTLEMENT_TITLE,
  ),
  [ShipmentMethodName.MANUAL_LABEL]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_MANUAL_LABEL_TITLE,
    COMMON.ShipmentMethodName_MANUAL_LABEL_TITLE,
  ),
  [ShipmentMethodName.DROPOFF]: t("Easy drop-off for fastest refund"),
});

export const getShipmentMethodTypeToSubtitleText = (
  copyResource?: Resource,
): Record<ShipmentMethodName, string> => ({
  [ShipmentMethodName.PICKUP]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_PICKUP_SUBTEXT,
    COMMON.ShipmentMethodName_PICKUP_SUBTEXT,
  ),
  [ShipmentMethodName.IN_STORE]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_IN_STORE_SUBTEXT,
    COMMON.ShipmentMethodName_IN_STORE_SUBTEXT,
  ),
  [ShipmentMethodName.SELF_SHIP]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_SELF_SHIP_SUBTEXT,
    COMMON.ShipmentMethodName_SELF_SHIP_SUBTEXT,
  ),
  [ShipmentMethodName.HAPPY_RETURNS]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_HAPPY_RETURNS_SUBTEXT,
    COMMON.ShipmentMethodName_HAPPY_RETURNS_SUBTEXT,
  ),
  [ShipmentMethodName.BLUE_YONDER]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_BLUE_YONDER_SUBTEXT,
    COMMON.ShipmentMethodName_BLUE_YONDER_SUBTEXT,
  ),
  [ShipmentMethodName.RETURN_BEAR]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_RETURN_BEAR_SUBTEXT,
    COMMON.ShipmentMethodName_RETURN_BEAR_SUBTEXT,
  ),
  [ShipmentMethodName.SETTLEMENT]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_SETTLEMENT_SUBTEXT,
    COMMON.ShipmentMethodName_SETTLEMENT_SUBTEXT,
  ),
  [ShipmentMethodName.MANUAL_LABEL]: localizeCopyResourceText(
    copyResource?.ShipmentMethodName_MANUAL_LABEL_SUBTEXT,
    COMMON.ShipmentMethodName_MANUAL_LABEL_SUBTEXT,
  ),
  [ShipmentMethodName.DROPOFF]: t(
    "Drop off your item at a nearby location. No packaging or printing required.",
  ),
});

export const useShipmentMethod = (
  navigation: Navigator,
  settings: CustomerPortalAppSettings | undefined,
  channel: Channel,
  copyResource?: Resource,
): useShipmentMethodResponse => {
  const { t } = useTranslation();
  const returnContext = useContext(ReturnContext);
  const client = useContext(RpcClientContext);
  const shippingEstimateContext = useContext(ShippingEstimateContext);

  const [isUpdatingMethod, setIsUpdatingMethod] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<
    ShipmentMethod | undefined
  >();

  const [shipmentMethodOptions, setShipmentMethodOptions] = useState<
    ShipmentMethodOption[] | undefined
  >();

  const [shipmentMethodError, setShipmentMethodError] = useState<
    string | undefined
  >();

  const getShippingMethodOptions = useHandler(async () => {
    try {
      setShipmentMethodError(undefined);
      const draftReturn = returnContext.draftReturn;
      if (!draftReturn || !client) {
        console.error("shipmentMethodOptions: No draft return or client");
        console.error("shipmentMethodOptions: client:", client);
        console.error(
          "shipmentMethodOptions: draftReturn:",
          returnContext.draftReturn,
        );
        return [];
      }

      const load = await getShippingMethodOptionsUtil(
        draftReturn,
        shippingEstimateContext,
        settings,
      );
      return load;
    } catch (e: any) {
      setShipmentMethodError(
        t(
          "Error retrieving shipment data, please back out and try again: {{errorMessage}}",
          { errorMessage: e?.message },
        ),
      );
      return [];
    }
  });

  useEffect(() => {
    void getShippingMethodOptions().then(setShipmentMethodOptions);
  }, [returnContext.draftReturn, client, getShippingMethodOptions]);

  const selectShipmentMethod = useCallback(
    async function selectShipmentMethod(selection: ShipmentMethod) {
      console.log("selectShipmentMethod", selection);
      const draftReturn = returnContext.draftReturn;
      if (!client || !draftReturn || !settings) {
        console.error(
          "selectShipmentMethod: client, draftReturn, or settings is undefined",
        );
        console.error("selectShipmentMethod: client:", client);
        console.error("selectShipmentMethod: draftReturn:", draftReturn);
        console.error("selectShipmentMethod: settings:", settings);
        return;
      }

      try {
        setIsUpdatingMethod(true);

        console.log(
          "selectShipmentMethod: setting shipment method to",
          selection,
        );
        const { draftReturn: updatedDraftReturn } =
          await client.setShipmentMethodInfo({
            draftReturnId: draftReturn._id,
            shipmentMethod: selection,
          });
        //It's possible that this endpoint fetches shipping estimates if it has not been fetched yet
        if (updatedDraftReturn.shipments) {
          shippingEstimateContext.updateShipments(
            Promise.resolve(updatedDraftReturn.shipments),
          );
        } else {
          shippingEstimateContext.deleteCache();
        }

        console.log(
          "selectShipmentMethod: updated draft return:",
          updatedDraftReturn,
        );
        returnContext.setDraftReturn(updatedDraftReturn);

        const isPickupAnOption = shipmentMethodOptions?.find(
          (option) => option.type === ShipmentMethodName.PICKUP,
        );

        const packagePickupUpsellDecisionContext = {
          selectionMethod: selection.method,
          isPickupAnOption: !!isPickupAnOption,
          pickupFee: isPickupAnOption?.pickupFee?.toNumber(),
        };

        if (
          selection.method === ShipmentMethodName.SELF_SHIP &&
          isPickupAnOption &&
          isPickupAnOption.pickupFee
        ) {
          datadogLogs.logger.info(
            `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${DECIDE_TO_OFFER_PICKUP_UPSELL_METRICS_LOG_STAMP} ${draftReturn._id} yes - offering pickup upsell`,
            packagePickupUpsellDecisionContext,
          );
          navigation.push(RouteName.PACKAGE_PICKUP_UPSELL, {
            pickupFee: isPickupAnOption.pickupFee.toNumber(),
            pickupCoveredByMerchant: isPickupAnOption.pickupCoveredByMerchant,
          });
          return;
        } else {
          datadogLogs.logger.info(
            `${PACKAGE_PICKUP_UPSELL_METRICS_LOG_STAMP} ${DECIDE_NOT_TO_OFFER_PICKUP_UPSELL_METRICS_LOG_STAMP} ${draftReturn._id} no - not offering pickup upsell`,
            packagePickupUpsellDecisionContext,
          );
        }

        navigateFromStack(
          await getStepsInReturn(
            updatedDraftReturn,
            settings,
            () =>
              client.calculateReturnTotals({ draftReturnId: draftReturn._id! }),
            channel,
            shippingEstimateContext,
          ),
          StepInReturn.CHOOSE_SHIPMENT_METHOD,
          navigation,
        );
      } finally {
        setIsUpdatingMethod(false);
      }
    },
    [
      channel,
      client,
      returnContext,
      setIsUpdatingMethod,
      navigation,
      settings,
      shipmentMethodOptions,
      shippingEstimateContext,
    ],
  );

  // Make method.type have to match one of the methodsToDisplay
  type ShipmentMethodOptionToDisplay = Omit<ShipmentMethodOption, "type"> & {
    type: Extract<ShipmentMethodName, MethodToDisplay>;
  };

  function getMethodInfo(
    method: ShipmentMethodOptionToDisplay,
  ): ShipmentMethodItem {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const rightTitleText = (text: string): string => {
      return text;
    };

    const titleTexts = getShipmentMethodTypeToTitleText(copyResource);
    const subtitleTexts = getShipmentMethodTypeToSubtitleText(copyResource);
    const blueYonderlocationsText = getBlueYonderLocationsText(copyResource);

    const currency = returnContext.draftReturn?.currency ?? Currency.USD;
    const presentmentCurrency =
      returnContext.draftReturn?.presentmentCurrency ?? currency;

    const item: Omit<ShipmentMethodItem, "selected" | "onSelect"> | null =
      (function () {
        switch (method.type) {
          case ShipmentMethodName.IN_STORE:
            return {
              title:
                copyResource?.IN_STORE_RETURN_TEXT ||
                titleTexts[ShipmentMethodName.IN_STORE],
              rightTitle: rightTitleText(t("Free")),
              subtitle:
                copyResource?.IN_STORE_RETURN_SUBTEXT ||
                subtitleTexts[ShipmentMethodName.IN_STORE],
              subtitle2: t("Closest in store locations:"),
              selection: { method: ShipmentMethodName.IN_STORE },
              icon: IconType.IN_STORE,
              locations: method.inStoreLocations,
            };
          case ShipmentMethodName.SELF_SHIP:
            return {
              title: titleTexts[ShipmentMethodName.SELF_SHIP],
              rightTitle: rightTitleText(
                method.nonPickupFee === undefined ||
                  method.nonPickupFee.toNumber() === 0
                  ? t("Free")
                  : moneyFormat({
                      amount: method.nonPickupFee.toNumber(),
                      currency:
                        returnContext.draftReturn?.currency ?? Currency.USD,
                    }),
              ),
              fee: method.customerUpchargeFee
                ? formatPriceAdjustment(
                    method.customerUpchargeFee.toNumber(),
                    presentmentCurrency,
                  ).formattedPriceAdjustment
                : undefined,
              subtitle: subtitleTexts[ShipmentMethodName.SELF_SHIP],
              selection: { method: ShipmentMethodName.SELF_SHIP },
              icon: IconType.SELF_SHIP,
            };
          case ShipmentMethodName.BLUE_YONDER:
            return {
              title: titleTexts[ShipmentMethodName.BLUE_YONDER],
              rightTitle: rightTitleText(t("Free")),
              subtitle: subtitleTexts[ShipmentMethodName.BLUE_YONDER],
              subtitle2: blueYonderlocationsText,
              selection: { method: ShipmentMethodName.BLUE_YONDER },
              icon: IconType.BLUE_YONDER,
              locations: method.blueYonderDetails?.locations.map(
                (location) => ({
                  name: location.locationName,
                  address: {
                    address1: location.address.street1,
                    address2: location.address.street2 ?? undefined,
                    city: location.address.city,
                    state: location.address.state ?? undefined,
                    zip: location.address.zip,
                    country: location.address.country,
                  },
                  distance: location.distance,
                }),
              ),
              privacyPolicyText: method.blueYonderDetails?.privacyPolicyText,
            };
          case ShipmentMethodName.RETURN_BEAR:
            return {
              title: titleTexts[ShipmentMethodName.RETURN_BEAR],
              rightTitle: rightTitleText(t("Free")),
              subtitle: subtitleTexts[ShipmentMethodName.RETURN_BEAR],
              subtitle2: t("Closest drop-off locations:"),
              selection: { method: ShipmentMethodName.RETURN_BEAR },
              icon: IconType.RETURN_BEAR,
              locations: method.returnBearDetails?.locations.map(
                (location) => ({
                  name: location.name,
                  address: {
                    address1: location.address.street1,
                    address2: location.address.street2,
                    city: location.address.city,
                    state: location.address.state,
                    zip: location.address.zip,
                    country: location.address.country,
                  },
                  distance: location.distance,
                  geo: location.geo,
                }),
              ),
            };
          case ShipmentMethodName.MANUAL_LABEL:
            return {
              title: titleTexts[ShipmentMethodName.MANUAL_LABEL],
              rightTitle: rightTitleText(t("To be determined")),
              subtitle: subtitleTexts[ShipmentMethodName.MANUAL_LABEL],
              selection: { method: ShipmentMethodName.MANUAL_LABEL },
              icon: IconType.SELF_SHIP,
            };
          case ShipmentMethodName.DROPOFF:
            return {
              title: titleTexts[ShipmentMethodName.DROPOFF],
              rightTitle: rightTitleText(t("Free")),
              subtitle: subtitleTexts[ShipmentMethodName.DROPOFF],
              subtitle2: t("Locations near you:"),
              selection: { method: ShipmentMethodName.DROPOFF },
              icon: IconType.DROPOFF,
              locations: method.dropoffs?.map((location) => ({
                name: location.name,
                address: {
                  address1: location.address.street1,
                  address2: location.address.street2,
                  city: location.address.city,
                  state: location.address.state,
                  zip: location.address.zip,
                  country: location.address.country,
                },
                distance: location.distance,
                geo: location.geo,
              })),
            };
          default:
            assertNever(method.type);
        }
      })();
    return {
      ...item,
      selected: method.type === selectedMethod?.method,
      onSelect: () => setSelectedMethod(item.selection),
    };
  }

  function getTotalFee(method: ShipmentMethodOption): number {
    return (
      method.pickupFee?.toNumber() ?? 0 + (method.nonPickupFee?.toNumber() ?? 0)
    );
  }

  const sortedFilteredOptions = shipmentMethodOptions
    ? shipmentMethodOptions
        .sort((a, b) =>
          a.type === ShipmentMethodName.BLUE_YONDER
            ? -1
            : getTotalFee(a) - getTotalFee(b),
        )
        .filter((method): method is ShipmentMethodOptionToDisplay =>
          isMethodToDisplay(method.type),
        )
    : [];

  // Auto-select the first drop-off option if available and nothing selected
  if (!selectedMethod && sortedFilteredOptions.length > 0) {
    const dropOffOption = sortedFilteredOptions.find(
      (option) =>
        option.type === ShipmentMethodName.DROPOFF ||
        option.type === ShipmentMethodName.BLUE_YONDER ||
        option.type === ShipmentMethodName.IN_STORE ||
        option.type === ShipmentMethodName.RETURN_BEAR,
    );
    if (dropOffOption) {
      setSelectedMethod({ method: dropOffOption.type });
    }
  }

  return {
    texts: {
      continueText: isUpdatingMethod ? t("Loading...") : t("Continue"),
      previousPage: t("Previous page"),
      howYouWant: copyResource?.HOW_YOU_WANT ?? COMMON.HOW_YOU_WANT,
      pickBestMethod: copyResource?.PICK_BEST_METHOD ?? COMMON.PICK_BEST_METHOD,
    },
    selectedMethod,
    setSelectedMethod,
    onSelectShipmentMethod: (method) =>
      selectShipmentMethod(method ?? selectedMethod!),
    isLoadingShipmentOptions: shipmentMethodOptions === undefined,
    shipmentMethodOptions: sortedFilteredOptions.map((method) =>
      getMethodInfo(method),
    ),
    rawShipmentMethodOptions: sortedFilteredOptions,
    shouldDisableSelectShipmentMethod: !selectedMethod || isUpdatingMethod,
    shipmentMethodError,
    submitting: isUpdatingMethod,
  };
};
